import React, { useEffect, useMemo, useState } from 'react';
import { api, type ProfileListItem } from '../lib/api';
import { OperaWindow, OperaSection, OperaField } from './OperaWindow';

/** Presets from the spec, plus free-text Custom. */
const PRESETS = [15, 30, 45, 60, 120];
const MAX_MINUTES = 12 * 60;

interface Props {
  roomId: string;
  roomNumber: string;
  roomDisplayName: string | null;
  /** Providers already in a room; listed but not selectable. */
  busyProfileIds: Set<string>;
  onClose: () => void;
  onStarted: () => void;
}

export function StartBookingModal({
  roomId, roomNumber, roomDisplayName, busyProfileIds, onClose, onStarted,
}: Props) {
  const [profiles, setProfiles] = useState<ProfileListItem[]>([]);
  const [loading, setLoading]   = useState(true);
  const [query, setQuery]       = useState('');
  const [profileId, setProfileId] = useState<string | null>(null);
  const [minutes, setMinutes]   = useState<number>(30);
  const [customMinutes, setCustomMinutes] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.profiles('', false)
      .then(p => { if (!cancelled) setProfiles(p); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = q
      ? profiles.filter(p =>
          p.workingName.toLowerCase().includes(q) || p.aliases.some(a => a.toLowerCase().includes(q)))
      : profiles;
    // Available first, busy last. Busy providers stay listed rather than being
    // filtered out, so it reads as "she is engaged" instead of "she is missing".
    return [...matched].sort((a, b) => {
      const aBusy = busyProfileIds.has(a.id) ? 1 : 0;
      const bBusy = busyProfileIds.has(b.id) ? 1 : 0;
      if (aBusy !== bBusy) return aBusy - bBusy;
      return a.workingName.localeCompare(b.workingName);
    });
  }, [profiles, query, busyProfileIds]);

  const effectiveMinutes = isCustom ? parseInt(customMinutes, 10) : minutes;
  const minutesValid = Number.isInteger(effectiveMinutes) && effectiveMinutes >= 1 && effectiveMinutes <= MAX_MINUTES;

  async function handleStart() {
    if (!profileId || !minutesValid) return;
    setBusy(true);
    setError(null);
    try {
      await api.startBooking({ roomId, profileId, durationMinutes: effectiveMinutes });
      onStarted();
    } catch (e) {
      // A 409 is the one-room-at-a-time rule, and the server's message names
      // the room she is actually in — far more use than a generic failure, so
      // ApiError's message is surfaced verbatim rather than being replaced.
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const roomDesc = roomDisplayName ? `Room ${roomNumber} — ${roomDisplayName}` : `Room ${roomNumber}`;

  return (
    <OperaWindow
      title="ROOM TRACKER"
      subtitle={roomDesc}
      onClose={onClose}
      size="compact"
      sideButtons={[
        { label: busy ? 'Starting…' : 'Start', onClick: handleStart, primary: true, disabled: busy || !profileId || !minutesValid },
        { label: 'Cancel', onClick: onClose, disabled: busy },
      ]}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Service Provider">
        <OperaField label="Search">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="type a name…"
            autoFocus
          />
        </OperaField>
        <div className="rt-provider-list">
          {loading && <div className="rt-empty">Loading providers…</div>}
          {!loading && visible.length === 0 && <div className="rt-empty">No providers match.</div>}
          {visible.map(p => {
            const isBusy = busyProfileIds.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                className={`rt-provider ${profileId === p.id ? 'selected' : ''} ${isBusy ? 'busy' : ''}`}
                disabled={isBusy}
                onClick={() => setProfileId(p.id)}
                title={isBusy ? 'Already in a room' : undefined}
              >
                <span className="rt-provider-name">{p.workingName}</span>
                {isBusy && <span className="rt-provider-busy">in a room</span>}
              </button>
            );
          })}
        </div>
      </OperaSection>

      <OperaSection title="Duration">
        <div className="rt-duration-row">
          {PRESETS.map(m => (
            <button
              key={m}
              type="button"
              className={`rt-duration ${!isCustom && minutes === m ? 'selected' : ''}`}
              onClick={() => { setIsCustom(false); setMinutes(m); }}
            >
              {m >= 60 ? `${m / 60}h` : `${m}m`}
            </button>
          ))}
          <button
            type="button"
            className={`rt-duration ${isCustom ? 'selected' : ''}`}
            onClick={() => setIsCustom(true)}
          >
            Custom
          </button>
          {isCustom && (
            <input
              className="rt-custom-minutes"
              type="number"
              min={1}
              max={MAX_MINUTES}
              placeholder="min"
              value={customMinutes}
              onChange={e => setCustomMinutes(e.target.value)}
              autoFocus
            />
          )}
        </div>
        {isCustom && customMinutes !== '' && !minutesValid && (
          <div className="rt-hint-error">
            Enter a whole number of minutes between 1 and {MAX_MINUTES}.
          </div>
        )}
      </OperaSection>

      <div style={{ fontSize: 11, color: '#555', marginTop: 8 }}>
        The countdown starts immediately. Expiry does not end the booking — it stays
        red until someone presses End.
      </div>
    </OperaWindow>
  );
}
